/**
 * SE-2832: which network of its program a rotation belongs to.
 *
 * A program can sit under several networks, each with its own dashboard. A
 * rotation in one network is shown only in that network's dashboard, and its
 * directors who are not program admins see no other network. "Whole program"
 * (null) keeps today's behaviour.
 *
 * The choices are the program's networks as simpl_auth lists them with the
 * rotations (`networks` on GET person-groups) — the same table it checks a
 * network against, so the select cannot offer one the save would refuse.
 * Rendered only when there is a choice: a program under one network has
 * nothing to confine a rotation to.
 */
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { ProgramNetwork } from "./simplAuthAPI";

/** The select's value for "the whole program" (the column's null). */
const WHOLE_PROGRAM = "";

/** Whether a program's rotations can be confined to one network. */
export const offersNetworkChoice = (networks: ProgramNetwork[] | undefined) =>
  (networks?.length ?? 0) >= 2;

export const RotationNetworkSelect = ({
  networks,
  value,
  onChange,
  disabled = false,
  testId,
}: {
  networks: ProgramNetwork[];
  /** The rotation's network id, or null for the whole program. */
  value: string | null;
  onChange: (networkId: string | null) => void;
  disabled?: boolean;
  testId: string;
}) => {
  const { t } = useTranslation();
  if (!offersNetworkChoice(networks)) return null;
  const labelId = `${testId}-label`;
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      {/* Always shrunk: "Whole program" is the empty value, and an unshrunk
          label would sit on top of it. */}
      <InputLabel id={labelId} shrink>
        {t("Network")}
      </InputLabel>
      <Select
        labelId={labelId}
        label={t("Network")}
        notched
        value={value ?? WHOLE_PROGRAM}
        onChange={(e) => {
          const next = String(e.target.value);
          onChange(next === WHOLE_PROGRAM ? null : next);
        }}
        displayEmpty
        data-testid={testId}
      >
        <MenuItem value={WHOLE_PROGRAM}>{t("Whole program (every network)")}</MenuItem>
        {networks.map((network) => (
          <MenuItem key={network.id} value={network.id}>
            {network.name || network.id}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {t(
          "A rotation in one network is shown only in that network's dashboard. Its directors who are not program admins see no other network.",
        )}
      </FormHelperText>
    </FormControl>
  );
};
